import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";
import type { ImportSummary } from "components/ReportImportModal";

export interface PendingBalanceItem {
  _id: string;
  email: string;
  leaveType: string;
  balanceKey: string;
  balanceCount: number;
  effectiveDate: string;
  fiscalYear: number;
  currentAllocated: number;
  currentBalance: number;
  status: string;
  createdAt: string;
}

const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const useDownloadBalanceTemplate = () =>
  useMutation<void, AxiosError>(
    async () => {
      const res = await customAxios.get(
        "/reports/leaves/balance/import/template",
        { responseType: "blob" }
      );
      downloadBlob(res.data as Blob, "Balance_Import_Template.xlsx");
    },
    {
      onError: () =>
        showNotification({
          title: "Download failed",
          message: "Could not download the template.",
          color: "red",
        }),
    }
  );

const importBalance = async (file: File): Promise<ImportSummary> => {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await customAxios.post<{ success: boolean; data: ImportSummary }>(
    "/reports/leaves/balance/import",
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return data.data;
};

export const useImportBalanceReport = () =>
  useMutation<ImportSummary, AxiosError, File>(importBalance, {
    onError: (error) =>
      showNotification({
        title: "Import failed",
        message:
          (error?.response?.data as any)?.message || "Could not import the file.",
        color: "red",
      }),
  });

export const usePendingBalanceImports = (enabled: boolean) =>
  useQuery<{ items: PendingBalanceItem[] }, AxiosError>(
    ["pending-balance-imports"],
    async () => {
      const { data } = await customAxios.get<{
        success: boolean;
        data: { items: PendingBalanceItem[] };
      }>("/reports/leaves/balance/import/pending");
      return data.data;
    },
    { enabled }
  );

export const useReviewBalanceImports = () => {
  const qc = useQueryClient();
  return useMutation<
    { approved: number; ignored: number; skipped: number },
    AxiosError,
    { ids: string[]; action: "approve" | "ignore" }
  >(
    async (payload) => {
      const { data } = await customAxios.post<{
        success: boolean;
        data: { approved: number; ignored: number; skipped: number };
      }>("/reports/leaves/balance/import/review", payload);
      return data.data;
    },
    {
      onSuccess: () => qc.invalidateQueries(["pending-balance-imports"]),
      onError: (error) =>
        showNotification({
          title: "Review failed",
          message:
            (error?.response?.data as any)?.message || "Could not update balances.",
          color: "red",
        }),
    }
  );
};
